import { useSyncExternalStore } from 'react';
import { isCompact } from './ui/compact';

/*
 * Full screen in a browser: on a phone the address bar and the system bars take a good part of
 * a screen held on its side, so the first touch takes the page full screen (and keeps it on its
 * side where the browser allows); the gear menu turns it off and on. An app added to the home
 * screen is full screen already (manifest.webmanifest), and so is the iPhone and iPad app
 * (vite --mode app), whose web view going full screen as well loses the notch's safe areas and
 * lays its own close button over the menu.
 */

const canFullscreen = () =>
  import.meta.env.MODE !== 'app' &&
  typeof document !== 'undefined' &&
  document.fullscreenEnabled === true;

/** Turns the page on its side and keeps it there (Android, while full screen). */
function lockLandscape(): void {
  const orientation = screen.orientation as ScreenOrientation & {
    lock?: (o: 'landscape') => Promise<void>;
  };
  orientation?.lock?.('landscape').catch(() => undefined);
}

export function enterFullscreen(): void {
  if (!canFullscreen() || document.fullscreenElement) return;
  void document.documentElement
    .requestFullscreen({ navigationUI: 'hide' })
    .then(lockLandscape)
    .catch(() => undefined);
}

export function toggleFullscreen(): void {
  if (document.fullscreenElement) void document.exitFullscreen().catch(() => undefined);
  else enterFullscreen();
}

/** On a phone (in a browser tab, not an installed app): full screen at the first touch. */
export function fullscreenOnFirstTouch(): void {
  if (!canFullscreen()) return;
  if (matchMedia('(display-mode: fullscreen), (display-mode: standalone)').matches) return;
  const first = (e: PointerEvent) => {
    if (e.pointerType !== 'touch') return;
    document.removeEventListener('pointerup', first, true);
    if (isCompact()) enterFullscreen();
  };
  document.addEventListener('pointerup', first, true);
}

function subscribe(onChange: () => void): () => void {
  document.addEventListener('fullscreenchange', onChange);
  return () => document.removeEventListener('fullscreenchange', onChange);
}

/** Whether full screen can be had here (null: not at all), and whether it is on. */
export function useFullscreen(): boolean | null {
  return useSyncExternalStore(subscribe, () =>
    canFullscreen() ? document.fullscreenElement !== null : null,
  );
}
